import { CapacitorInAppPurchase } from '@adplorg/capacitor-in-app-purchase';
import { api } from './api';
import {
  appleProductIdForPlan,
  APPLE_IAP_PRODUCT_IDS,
  type AppleIapPlan,
} from './appleIapProducts';
import { rememberRefCode } from './influencerTracking';
import { isNativeIOS } from './platform';

type VerifyResponse = {
  ok: boolean;
  plan: AppleIapPlan;
  aiCoachingEnabled: boolean;
  adFreeEnabled: boolean;
};

/** App Store Connect numeric Apple ID (from redeem URL). Override with VITE_APPLE_APP_STORE_ID. */
export function appleAppStoreId(): string {
  const fromEnv = (import.meta as ImportMeta & { env?: Record<string, string> }).env?.VITE_APPLE_APP_STORE_ID?.trim();
  return fromEnv || '6785338720';
}

function randomUuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function storedInfluencerCode(): string | null {
  try {
    return localStorage.getItem('ppiq_ref');
  } catch {
    return null;
  }
}

export function iosIapAvailable(): boolean {
  return isNativeIOS();
}

/** Load App Store product metadata (price strings, etc.). */
export async function loadAppleProducts() {
  if (!iosIapAvailable()) return [];
  const { products } = await CapacitorInAppPurchase.getProducts({
    productIds: [...APPLE_IAP_PRODUCT_IDS],
  });
  return products;
}

async function verifyTransaction(transaction: string): Promise<VerifyResponse> {
  return api<VerifyResponse>('/api/apple/verify-transaction', {
    method: 'POST',
    body: JSON.stringify({
      transaction,
      influencerCode: storedInfluencerCode(),
    }),
  });
}

/** Purchase a subscription on iOS and unlock entitlements on the server. */
export async function purchaseAppleSubscription(plan: AppleIapPlan): Promise<VerifyResponse> {
  const productId = appleProductIdForPlan(plan);
  const { transaction } = await CapacitorInAppPurchase.purchaseSubscription({
    productId,
    referenceUUID: randomUuid(),
  });
  if (!transaction?.trim()) {
    throw new Error('Purchase completed but no transaction was returned.');
  }
  return verifyTransaction(transaction);
}

/** Restore active App Store subscriptions for the signed-in account. */
export async function restoreApplePurchases(): Promise<VerifyResponse | null> {
  const { subscriptions } = await CapacitorInAppPurchase.getActiveSubscriptions();
  if (!subscriptions?.length) return null;
  return api<VerifyResponse>('/api/apple/restore', {
    method: 'POST',
    body: JSON.stringify({
      transactions: subscriptions,
      influencerCode: storedInfluencerCode(),
    }),
  });
}

/** Open Apple's subscription management sheet. */
export async function manageAppleSubscriptions(subscriptionGroupId?: string): Promise<void> {
  await CapacitorInAppPurchase.manageSubscriptions({ subscriptionGroupId });
}

/** Save promo/influencer code for attribution (does not open Safari). */
export async function applyAppleOfferCode(rawCode: string): Promise<{ code: string }> {
  const code = rawCode
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9_-]/g, '');
  if (!code) throw new Error('Enter an offer code.');
  rememberRefCode(code);
  try {
    await api('/api/auth/apply-influencer-code', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  } catch {
    /* ASC-only codes may not match a web influencer row */
  }
  return { code };
}
